// Shared behaviour for every dSlip year dropdown: the ranges, and the
// menu-scroll that puts the current year in view.
//
// Two ranges, because the pages fall into two groups:
//
//   FULL WINDOW (2018 - rolling ceiling)
//     Salary Structure, Form-16 Files, Healthcard Files. These configure or
//     archive things that legitimately extend into the future - a salary
//     revision can be dated years ahead.
//
//   UP TO CURRENT YEAR (2018 - this year)
//     Generate Payslips, Payslip Management. You cannot generate or manage a
//     payslip for a year that hasn't happened yet, so offering future years
//     would only invite mistakes.
//
// Keeping both in one file means the range is changed in one place instead of
// five, and the two groups can't silently drift apart.

import React, { useEffect, useRef } from "react";
import { components } from "react-select";

export const DSLIP_YEAR_MIN = 2018;

const YEAR_MAX_BASE = 2050;
const YEAR_MAX_EXTEND = 50;

// The ceiling stays at 2050 until we actually reach it, then becomes
// (current year + 50) and moves with every year after that. Computed rather
// than hardcoded, because a fixed 2050 would quietly become "this year" in
// 2050 and a past date after that, leaving no future year to pick at all.
export function dslipYearMax() {
    const year = new Date().getFullYear();
    return year >= YEAR_MAX_BASE ? year + YEAR_MAX_EXTEND : YEAR_MAX_BASE;
}

// Ascending by default. Pass { desc: true } for newest-first, or override
// min/max for a narrower window.
export function dslipYears({ desc = false, min = DSLIP_YEAR_MIN, max = dslipYearMax() } = {}) {
    const years = [];
    for (let y = min; y <= max; y++) years.push(y);
    return desc ? years.reverse() : years;
}

// 2018 .. current year - never a future year. Newest-first by default, which
// is what the Generate / Management filters want.
export function dslipYearsToDate({ desc = true, min = DSLIP_YEAR_MIN } = {}) {
    return dslipYears({ desc, min, max: new Date().getFullYear() });
}

// ── Menu scroll ─────────────────────────────────────────────────────────────
// The lists run newest-first from the ceiling, so the current year sits ~25
// rows down and had to be scrolled to every time. These two components tag the
// current year and scroll the menu straight to it on open.

const YearOption = (props) => (
    <components.Option
        {...props}
        innerProps={{
            ...props.innerProps,
            // The chosen year wins as the scroll target; the current year is
            // the fallback for a picker that hasn't been used yet.
            "data-dslip-sel": props.isSelected ? "1" : undefined,
            "data-dslip-now":
                String(props.data?.value) === String(new Date().getFullYear()) ? "1" : undefined,
        }}
    />
);

const YearMenuList = (props) => {
    const listRef = useRef(null);

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        // Scroll to the SELECTED year, or the current year when nothing is
        // chosen yet. react-select does not do this for us here - the menu is
        // portaled to <body>, and it reopens at the top of the list every
        // time, which put the ceiling year in view instead of the choice.
        const target =
            list.querySelector('[data-dslip-sel="1"]') ||
            list.querySelector('[data-dslip-now="1"]');
        if (!target) return;
        // Centre it rather than pinning it to the top, so the years either
        // side are visible and it's obvious where you are in the list.
        const offset = target.offsetTop - list.offsetTop;
        list.scrollTop = Math.max(0, offset - (list.clientHeight - target.offsetHeight) / 2);
    }, [props.hasValue]);

    return (
        <components.MenuList
            {...props}
            innerRef={(el) => {
                listRef.current = el;
                if (typeof props.innerRef === "function") props.innerRef(el);
            }}
        />
    );
};

// Spread onto any year <Select> to get the scroll-to-current-year behaviour.
export const DSLIP_YEAR_SELECT = {
    components: { Option: YearOption, MenuList: YearMenuList },
};
